const OPTION_SELECTOR = "[data-dropdown-option]";

class SubscriptionSelector extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector("[data-dropdown-trigger]");
    this.panel = this.querySelector("[data-dropdown-panel]");
    this.valueEl = this.querySelector("[data-trigger-value]");
    this.options = Array.from(this.querySelectorAll(OPTION_SELECTOR));
    this.modeInputs = Array.from(this.querySelectorAll("[data-purchase-mode]"));
    this.planInput = this.querySelector("[data-selling-plan-input]");
    this.form = this.closest("form");
    this.productForm = this.closest("product-form");
    this.atcPrice = this.form?.querySelector(
      "[data-product-form-atc] price-snippet",
    );

    const dataEl = this.querySelector("[data-subscription-data]");
    this.data = dataEl ? JSON.parse(dataEl.textContent) : {};

    if (!this.trigger || !this.panel || !this.options.length) return;

    this.mode = this.modeInputs.find((input) => input.checked)?.value || "onetime";
    this.selectedPlanId = this.selectedOption?.dataset.sellingPlanId || null;

    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onPanelClick = this._onPanelClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onDocumentPointer = this._onDocumentPointer.bind(this);
    this._onModeChange = this._onModeChange.bind(this);
    this._onVariantChange = this._onVariantChange.bind(this);

    this.trigger.addEventListener("click", this._onTriggerClick);
    this.panel.addEventListener("click", this._onPanelClick);
    this.addEventListener("keydown", this._onKeydown);
    this.modeInputs.forEach((input) =>
      input.addEventListener("change", this._onModeChange),
    );
    this.productForm?.addEventListener("variant:changed", this._onVariantChange);

    if (this.mode === "subscribe") this._applyState();

    this.detailsTrigger = this.querySelector("[data-details-trigger]");
    this._onDetailsClick = this._onDetailsClick.bind(this);
    this.detailsTrigger?.addEventListener("click", this._onDetailsClick);
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener("click", this._onTriggerClick);
    this.panel?.removeEventListener("click", this._onPanelClick);
    this.removeEventListener("keydown", this._onKeydown);
    this.modeInputs?.forEach((input) =>
      input.removeEventListener("change", this._onModeChange),
    );
    this.productForm?.removeEventListener(
      "variant:changed",
      this._onVariantChange,
    );
    this.detailsTrigger?.removeEventListener("click", this._onDetailsClick);
    document.removeEventListener("pointerdown", this._onDocumentPointer);
  }

  _onDetailsClick() {
    const template = this.querySelector("[data-details-modal-content]");
    if (!template) return;
    const html = template.content.firstElementChild.cloneNode(true).outerHTML;
    this.detailsTrigger.dispatchEvent(
      new CustomEvent("markup-modal:open", {
        bubbles: true,
        detail: { html, target: "subscription-details" },
      }),
    );
  }

  get isOpen() {
    return this.trigger.getAttribute("aria-expanded") === "true";
  }

  get selectedOption() {
    return this.options.find(
      (option) => option.getAttribute("aria-selected") === "true",
    );
  }

  open() {
    if (this.isOpen) return;
    this.trigger.setAttribute("aria-expanded", "true");
    this.panel.hidden = false;
    document.addEventListener("pointerdown", this._onDocumentPointer);
    (this.selectedOption || this.options[0]).focus();
  }

  close({ focusTrigger = false } = {}) {
    if (!this.isOpen) return;
    this.trigger.setAttribute("aria-expanded", "false");
    this.panel.hidden = true;
    document.removeEventListener("pointerdown", this._onDocumentPointer);
    if (focusTrigger) this.trigger.focus();
  }

  selectOption(option) {
    if (!option) return;
    this.options.forEach((o) =>
      o.setAttribute("aria-selected", String(o === option)),
    );
    this.selectedPlanId = option.dataset.sellingPlanId;
    this.valueEl.textContent = option.dataset.triggerLabel;
    this._applyState();
    this.close({ focusTrigger: true });
  }

  _onModeChange() {
    this.mode = this.modeInputs.find((input) => input.checked)?.value || "onetime";
    this._applyState();
  }

  _onVariantChange() {
    if (this.mode !== "subscribe") return;
    queueMicrotask(() => this._applyState());
  }

  _currentVariantId() {
    return this.form?.querySelector('input[name="id"]')?.value;
  }

  _applyState() {
    const variantId = this._currentVariantId();
    const variant = this.data[variantId];
    if (!variant) return;

    const subscriptionPrice =
      this.mode === "subscribe" && this.selectedPlanId
        ? variant.plans[this.selectedPlanId]
        : null;

    const signature = `${variantId}|${this.mode}|${this.selectedPlanId}|${subscriptionPrice}`;
    if (signature === this._lastSignature) return;
    this._lastSignature = signature;

    if (subscriptionPrice != null) {
      this.planInput.value = this.selectedPlanId;
      this.planInput.disabled = false;
      this._setPrice(subscriptionPrice, variant.price);
    } else {
      this.planInput.value = "";
      this.planInput.disabled = true;
      this._setPrice(variant.price, variant.compareAt);
    }

    this._updateUrl();
  }

  _updateUrl() {
    if (window.Shopify?.designMode) return;
    const url = new URL(window.location);
    const variantId = this._currentVariantId();
    if (variantId) url.searchParams.set("variant", variantId);
    if (this.mode === "subscribe" && this.planInput.value) {
      url.searchParams.set("selling_plan", this.planInput.value);
    } else {
      url.searchParams.delete("selling_plan");
    }
    window.history.replaceState("", "", url);
  }

  _setPrice(price, compareAtPrice) {
    if (!this.atcPrice || price == null) return;
    this.atcPrice.setAttribute("price", price / 100);
    this.atcPrice.setAttribute("compare-at-price", (compareAtPrice || 0) / 100);
    this.atcPrice.setAttribute("currency", window.Shopify.currency.active);
    this.atcPrice.setAttribute("dynamic", "");
  }

  _focusByOffset(offset) {
    const currentIndex = this.options.indexOf(document.activeElement);
    const lastIndex = this.options.length - 1;
    let nextIndex = currentIndex + offset;
    if (nextIndex < 0) nextIndex = lastIndex;
    if (nextIndex > lastIndex) nextIndex = 0;
    this.options[nextIndex].focus();
  }

  _onTriggerClick(event) {
    event.preventDefault();
    this.isOpen ? this.close() : this.open();
  }

  _onPanelClick(event) {
    const option = event.target.closest(OPTION_SELECTOR);
    if (option) this.selectOption(option);
  }

  _onDocumentPointer(event) {
    if (!this.contains(event.target)) this.close();
  }

  _onKeydown(event) {
    const onTrigger = event.target === this.trigger;
    const onOption = this.options.includes(event.target);

    switch (event.key) {
      case "ArrowDown":
        if (onTrigger && !this.isOpen) {
          event.preventDefault();
          this.open();
        } else if (onOption) {
          event.preventDefault();
          this._focusByOffset(1);
        }
        break;
      case "ArrowUp":
        if (onOption) {
          event.preventDefault();
          this._focusByOffset(-1);
        }
        break;
      case "Home":
        if (onOption) {
          event.preventDefault();
          this.options[0].focus();
        }
        break;
      case "End":
        if (onOption) {
          event.preventDefault();
          this.options[this.options.length - 1].focus();
        }
        break;
      case "Enter":
      case " ":
        if (onTrigger && !this.isOpen) {
          event.preventDefault();
          this.open();
        } else if (onOption) {
          event.preventDefault();
          this.selectOption(event.target);
        }
        break;
      case "Escape":
        if (this.isOpen) {
          event.preventDefault();
          this.close({ focusTrigger: true });
        }
        break;
      case "Tab":
        this.close();
        break;
    }
  }
}

if (!customElements.get("subscription-selector")) {
  customElements.define("subscription-selector", SubscriptionSelector);
}
